import { NEAR_INSTANT_DURATION_FACTOR, type LatLng } from "@strava/feature-global-wasm-mre";
import type { Camera, CameraMargins } from "@strava/mre";

import { logError } from "../../utils/sentry";

const FOLLOW_MARGINS: CameraMargins = { top: 0, right: 0, bottom: 0, left: 0 };
const TRANSLATE_DURATION_FACTOR = 1.2;

export function isValidLatLng(point: LatLng | null): point is LatLng {
  if (!point) return false;
  const [lat, lng] = point;
  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}

export function panCameraTo(camera: Camera, latLng: LatLng): void {
  const [lat, lng] = latLng;
  try {
    camera.translateTo(
      { latitude: lat, longitude: lng },
      TRANSLATE_DURATION_FACTOR,
      FOLLOW_MARGINS,
      undefined
    );
  } catch (error) {
    logError(error);
  }
}

// Used for the initial camera placement in the live Beacon view. Bypasses
// Map.Bounds so we can pass FOLLOW_MARGINS (zeros), matching panCameraTo.
// Same margins on init and on every subsequent translateTo means MRE
// preserves camera state across the transition — no zoom shift on the
// first follow update or recenter.
//
// We use flyToWithZoomLevel (Mapbox-style 0–22 zoom) instead of distance-based
// flyTo so the value is 1:1 comparable with the legacy Mapbox/Leaflet view's
// default zoom level (18) — no need to tune meters by trial and error.
//
// `durationFactor` defaults to NEAR_INSTANT_DURATION_FACTOR (visually
// instant, ~0.005). Pass a higher value for a smoother transition — e.g.
// 0.3 ("snappy") for the recenter button, matching the legacy Leaflet
// setView pan-and-zoom animation feel.
export function flyCameraTo(
  camera: Camera,
  latLng: LatLng,
  zoomLevel: number,
  durationFactor: number = NEAR_INSTANT_DURATION_FACTOR
): void {
  const [lat, lng] = latLng;
  try {
    const currentTarget = camera.getTarget();
    camera.flyToWithZoomLevel(
      lat,
      lng,
      zoomLevel,
      currentTarget.pitch,
      currentTarget.bearing,
      durationFactor,
      FOLLOW_MARGINS,
      undefined
    );
  } catch (error) {
    logError(error);
  }
}
